import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import PieChart from 'components/charts/PieChart';
import BarChart from 'components/charts/BarChart';

const MetricsDashboard = ({ siteId, ymetricaId, currentLang }) => {
  const { token, API_URL, getCsrfToken } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [yandexToken, setYandexToken] = useState(null);
  
  // Состояние для выбора периода
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 6); // 7 дней по умолчанию
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  });

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const setQuickPeriod = (period) => {
    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case 'today':
        startDate = today;
        endDate = today;
        break;
      case 'yesterday':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = today;
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        // Исправлено: последний день текущего месяца
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    setDateRange({
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    });
  };
  
  // Данные для диаграмм
  const [trafficSources, setTrafficSources] = useState({ series: [], options: {} });
  const [countries, setCountries] = useState({ series: [], options: {} });
  const [devices, setDevices] = useState({ series: [], options: {} });
  const [bounceRate, setBounceRate] = useState({ chartData: [], chartOptions: {} });

  const fetchYandexToken = async () => {
    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const tokenResponse = await fetch(`${API_URL}${currentLang}/yandex-metrica/token?site_id=${siteId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const tokenData = await tokenResponse.json();
      
      if (!tokenResponse.ok || !tokenData.success) {
        throw new Error(currentLang === 'ru' ? 'Не удалось получить токен доступа к Яндекс.Метрике' : 'Failed to get Yandex Metrica access token');
      }

      return tokenData.token;
    } catch (error) {
      throw error;
    }
  };

  const fetchMetricsData = async (token, endpoint, params) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `https://api-metrika.yandex.net/stat/v1/data?${queryString}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `OAuth ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Ошибка получения данных: ${response.status}`);
    }

    return await response.json();
  };

  const loadAllMetrics = async () => {
    if (!ymetricaId || ymetricaId === '0' || String(ymetricaId).trim() === '') {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await fetchYandexToken();
      setYandexToken(token);

      // Используем выбранный период
      const { startDate: date1, endDate: date2 } = dateRange;

      // 1. Источники трафика
      const trafficData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:visits',
        dimensions: 'ym:s:trafficSource',
        sort: '-ym:s:visits',
        limit: 10
      });

      if (trafficData.data && trafficData.data.length > 0) {
        const trafficSeries = trafficData.data.map(item => parseInt(item.metrics[0]));
        const trafficLabels = trafficData.data.map(item => {
          const source = item.dimensions[0].name;
          return currentLang === 'ru' ? 
            (source === 'organic' ? 'Поиск' : 
             source === 'referral' ? 'Переходы' : 
             source === 'direct' ? 'Прямой' : 
             source === 'advertising' ? 'Реклама' : source) : source;
        });

        setTrafficSources({
          series: trafficSeries,
          options: {
            labels: trafficLabels,
            colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'],
            legend: {
              position: 'bottom',
              fontSize: '12px'
            },
            tooltip: {
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' визитов' : ' visits');
                }
              }
            }
          }
        });
      }

      // 2. Страны
      const countriesData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:users',
        dimensions: 'ym:s:regionCountry',
        sort: '-ym:s:users',
        limit: 8
      });

      if (countriesData.data && countriesData.data.length > 0) {
        const countriesSeries = countriesData.data.map(item => parseInt(item.metrics[0]));
        const countriesLabels = countriesData.data.map(item => item.dimensions[0].name);

        setCountries({
          series: countriesSeries,
          options: {
            labels: countriesLabels,
            colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316'],
            legend: {
              position: 'bottom',
              fontSize: '12px'
            },
            tooltip: {
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' пользователей' : ' users');
                }
              }
            }
          }
        });
      }

      // 3. Устройства
      const devicesData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:visits',
        dimensions: 'ym:s:deviceCategory',
        sort: '-ym:s:visits'
      });

      if (devicesData.data && devicesData.data.length > 0) {
        const devicesSeries = devicesData.data.map(item => parseInt(item.metrics[0]));
        const devicesLabels = devicesData.data.map(item => {
          const device = item.dimensions[0].name;
          return currentLang === 'ru' ? 
            (device === 'desktop' ? 'Компьютер' : 
             device === 'mobile' ? 'Мобильный' : 
             device === 'tablet' ? 'Планшет' : device) : device;
        });

        setDevices({
          series: devicesSeries,
          options: {
            labels: devicesLabels,
            colors: ['#3B82F6', '#10B981', '#F59E0B'],
            legend: {
              position: 'bottom',
              fontSize: '12px'
            },
            tooltip: {
              y: {
                formatter: function(value) {
                  return value + (currentLang === 'ru' ? ' визитов' : ' visits');
                }
              }
            }
          }
        });
      }

      // 4. Динамика отказов за 30 дней
      const bounceData = await fetchMetricsData(token, 'data', {
        ids: ymetricaId,
        date1,
        date2,
        metrics: 'ym:s:bounceRate',
        dimensions: 'ym:s:date',
        sort: 'ym:s:date'
      });

      if (bounceData.data && bounceData.data.length > 0) {
        const categories = [];
        const seriesData = [];
        
        bounceData.data.forEach(item => {
          const date = new Date(item.dimensions[0].name);
          categories.push(date.toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
            day: '2-digit',
            month: '2-digit'
          }));
          seriesData.push(parseFloat(item.metrics[0]).toFixed(1));
        });

        setBounceRate({
          chartData: [{
            name: currentLang === 'ru' ? 'Процент отказов' : 'Bounce Rate',
            data: seriesData,
            color: "#EF4444",
          }],
          chartOptions: {
            legend: {
              show: false,
            },
            theme: {
              mode: "light",
            },
            chart: {
              type: "bar",
              toolbar: {
                show: false,
              },
            },
            dataLabels: {
              enabled: false,
            },
            plotOptions: {
              bar: {
                borderRadius: 4,
                columnWidth: '60%',
                distributed: false,
              },
            },
            tooltip: {
              style: {
                fontSize: "12px",
                fontFamily: undefined,
                backgroundColor: "#000000"
              },
              theme: 'dark',
              y: {
                formatter: function(value) {
                  return value + '%';
                }
              }
            },
            grid: {
              show: false,
            },
            xaxis: {
              axisBorder: {
                show: false,
              },
              axisTicks: {
                show: false,
              },
              labels: {
                style: {
                  colors: "#A3AED0",
                  fontSize: "10px",
                  fontWeight: "500",
                },
                rotate: -45,
                rotateAlways: false,
              },
              type: "text",
              categories: categories,
            },
            yaxis: {
              show: false,
            },
            colors: ["#EF4444"],
            fill: {
              type: "solid",
              opacity: 0.8
            }
          }
        });
      }

    } catch (error) {
      
      setError(error.message || 'Ошибка сети при получении данных метрики');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (siteId && ymetricaId && ymetricaId !== '0' && String(ymetricaId).trim() !== '') {
      loadAllMetrics();
    }
  }, [siteId, ymetricaId, currentLang, dateRange]);

  if (!ymetricaId || ymetricaId === '0' || String(ymetricaId).trim() === '') {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h3 className="text-lg font-bold text-gray-800">
          {currentLang === 'ru' ? 'Аналитика Яндекс.Метрики' : 'Yandex Metrica Analytics'}
        </h3>
        
        {/* Селектор периода */}
        <div className="flex flex-col space-y-3">
          {/* Быстрые кнопки */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setQuickPeriod('today')}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {currentLang === 'ru' ? 'Сегодня' : 'Today'}
            </button>
            <button
              onClick={() => setQuickPeriod('yesterday')}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {currentLang === 'ru' ? 'Вчера' : 'Yesterday'}
            </button>
            <button
              onClick={() => setQuickPeriod('week')}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {currentLang === 'ru' ? 'За неделю' : 'Last 7 days'}
            </button>
            <button
              onClick={() => setQuickPeriod('thisMonth')}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {currentLang === 'ru' ? 'Этот месяц' : 'This month'}
            </button>
            <button
              onClick={() => setQuickPeriod('lastMonth')}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {currentLang === 'ru' ? 'Прошлый месяц' : 'Last month'}
            </button>
          </div>
          
          {/* Ручной выбор дат */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">
              {currentLang === 'ru' ? 'Период:' : 'Period:'}
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <span className="text-gray-500">-</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="flex items-center space-x-2">
            <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></span>
            <span className="text-gray-600">
              {currentLang === 'ru' ? 'Загрузка данных...' : 'Loading data...'}
            </span>
          </div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadAllMetrics}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
            >
              {currentLang === 'ru' ? 'Повторить' : 'Retry'}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Источники трафика */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-md font-semibold mb-4 text-center">
              {currentLang === 'ru' ? 'Источники трафика' : 'Traffic Sources'}
            </h4>
            {trafficSources.series.length > 0 ? (
              <div className="h-64">
                <PieChart
                  series={trafficSources.series}
                  options={trafficSources.options}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                {currentLang === 'ru' ? 'Нет данных' : 'No data'}
              </div>
            )}
          </div>

          {/* Страны */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-md font-semibold mb-4 text-center">
              {currentLang === 'ru' ? 'Страны' : 'Countries'}
            </h4>
            {countries.series.length > 0 ? (
              <div className="h-64">
                <PieChart
                  series={countries.series}
                  options={countries.options}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                {currentLang === 'ru' ? 'Нет данных' : 'No data'}
              </div>
            )}
          </div>

          {/* Устройства */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-md font-semibold mb-4 text-center">
              {currentLang === 'ru' ? 'Устройства' : 'Devices'}
            </h4>
            {devices.series.length > 0 ? (
              <div className="h-64">
                <PieChart
                  series={devices.series}
                  options={devices.options}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                {currentLang === 'ru' ? 'Нет данных' : 'No data'}
              </div>
            )}
          </div>

          {/* Динамика отказов */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="text-md font-semibold mb-4 text-center">
              {currentLang === 'ru' ? 'Динамика отказов' : 'Bounce Rate Trend'}
            </h4>
            {bounceRate.chartData.length > 0 ? (
              <div className="h-64">
                <BarChart
                  chartData={bounceRate.chartData}
                  chartOptions={bounceRate.chartOptions}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                {currentLang === 'ru' ? 'Нет данных' : 'No data'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetricsDashboard; 